import { useQuery, useQueryClient } from "@tanstack/react-query";
import { keepPreviousData } from "@tanstack/react-query";

import { Bounds } from "@nens/deck-gl";
import { getRanaApiV1 } from "@nens/rana/api-v1";
import { combineUrlAndParams } from "@nens/shared-utils";

import { RasterFileDescriptor, ScenarioFileDescriptor } from "@/types/descriptor-types";

import { queryDirectly } from "./hooks/query-directly";
import { getSearchStatePublicationId } from "./utils/get-search-state-publication-id";

// Single

const getWmsGetColorRangeQuery = (
  descriptor: RasterFileDescriptor | ScenarioFileDescriptor | undefined,
  layer: string | undefined,
  bounds: Bounds | undefined
) => {
  const url = (() => {
    if (!layer || !bounds || !descriptor) return undefined;

    const publicationId = getSearchStatePublicationId();

    const baseUrl = publicationId
      ? `/tenants/${descriptor.tenant}/publications/${publicationId}/file-descriptors/${descriptor.id}/wms`
      : `/tenants/${descriptor.tenant}/file-descriptors/${descriptor.id}/wms`;

    return combineUrlAndParams(baseUrl, {
      service: "wms",
      request: "getlimits",
      version: "1.1.1",
      layers: layer,
      width: 256,
      height: 256,
      srs: "EPSG:4326",
      bbox: bounds.join(","),
    });
  })();

  const enabled = !!url;

  return getRanaApiV1().getQueryOptions({
    queryKey: ["color-range", "wms", url],
    queryFn: async () => {
      if (!url) throw Error("");
      const limits = (await getRanaApiV1().BaseApi.fetchJson(url)) as [[number, number]];

      return limits[0];
    },
    enabled,
    placeholderData: enabled ? keepPreviousData : undefined,
  });
};

export function useWmsGetColorRangeQuery(
  descriptor: RasterFileDescriptor | ScenarioFileDescriptor | undefined,
  layer: string | undefined,
  bounds: Bounds | undefined
) {
  return useQuery(getWmsGetColorRangeQuery(descriptor, layer, bounds));
}

export function useWmsGetColorRangeQueryDirectly() {
  const queryClient = useQueryClient();
  return (
    descriptor: RasterFileDescriptor | ScenarioFileDescriptor,
    layer: string,
    bounds: Bounds
  ) => queryDirectly(queryClient, getWmsGetColorRangeQuery(descriptor, layer, bounds));
}
